import React, { useEffect, useMemo, useState } from 'react'
import { CompanyProfileEnrichment } from './CompanyProfileEnrichment'
import { IndustryWorkspaceTemplates } from './IndustryWorkspaceTemplates'
import {ActivationGuidance} from './ActivationGuidance'
import type { CompanyProfileConfirmation } from '../../services/companyEnrichment'
import { useAccessContext } from '../../context/AccessContext'
import type { AccessibleLocation, OnboardingStepKey, TenantOnboardingState } from '../../types/access'
import {
  apiErrorMessage,
  completeOnboarding,
  getOnboardingReadiness,
  saveOnboardingCompanyProfile,
  saveOnboardingFirstJob,
  saveOnboardingGeofence,
  saveOnboardingBillingPlan,
  saveOnboardingLocations,
  saveOnboardingSupervisors,
  saveOnboardingWorkers,
  skipOnboarding,
  startOnboarding,
  updateOnboardingStatus,
  type CompanyType,
  type OperatingModel,
  type OnboardingLocationPayload,
  type OnboardingSupervisorPayload,
  type OnboardingWorkerPayload,
} from '../../services/access'
import { navigateTo } from '../../services/navigation'
import { normalizeLocationAddress } from '../../services/locationAddress'
import { LeafletGeofencePicker } from '../workspaces/mapper/LeafletGeofencePicker'
import { useI18n } from '../../i18n/I18nContext'

const steps: Array<{ key: OnboardingStepKey; label: string; optional?: boolean; actionUrl?: string }> = [
  { key: 'workspace_profile', label: 'Welcome / workspace', actionUrl: '/admin/organizations' },
  { key: 'organization_profile', label: 'Organization profile', actionUrl: '/admin/organizations' },
  { key: 'branding', label: 'Branding', actionUrl: '/admin/settings' },
  { key: 'locations', label: 'Locations', actionUrl: '/admin/locations' },
  { key: 'geofences', label: 'Geofences', actionUrl: '/admin/locations' },
  { key: 'users_roles', label: 'Admins & roles', actionUrl: '/admin/users-access' },
  { key: 'workers', label: 'Workers', actionUrl: '/admin/workers' },
  { key: 'first_job', label: 'First job/order', actionUrl: '/admin/jobs' },
  { key: 'first_shift', label: 'First shift', actionUrl: '/admin/schedule' },
  { key: 'approvals', label: 'Approvals', actionUrl: '/admin/timesheets' },
  { key: 'exports', label: 'Reporting/export', actionUrl: '/admin/payroll' },
  { key: 'billing_plan', label: 'Free plan & optional acceleration', optional: true, actionUrl: '/billing' },
  { key: 'readiness', label: 'Readiness checklist', actionUrl: '/admin' },
]

const companyTypes: CompanyType[] = ['construction', 'staffing_agency', 'security', 'restaurant_franchise', 'warehouse_logistics', 'retail', 'other']
const operatingModels: OperatingModel[] = ['company_only', 'agency_only', 'company_with_agencies', 'subcontractor_network']
const DEFAULT_GEOFENCE_RADIUS_METERS = 10
const MIN_GEOFENCE_RADIUS_METERS = 1
const MAX_GEOFENCE_RADIUS_METERS = 5000
const GEOCODE_ERROR = "We couldn't find that address. Drop the pin on the map or adjust the address."

const blankLocation = (): OnboardingLocationPayload => ({
  name: '',
  address: '',
  city: '',
  region: '',
  country: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  defaultPunchRadius: DEFAULT_GEOFENCE_RADIUS_METERS,
})

const blankSupervisor = (): OnboardingSupervisorPayload => ({ email: '', phone: '', firstName: '', lastName: '' })
const blankWorker = (): OnboardingWorkerPayload => ({ email: '', phone: '', firstName: '', lastName: '' })

const humanize = (value: string) => value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())

const firstNumber = (...values: unknown[]) => {
  for (const value of values) {
    const numeric = Number(value)
    if (Number.isFinite(numeric)) return numeric
  }
  return undefined
}

const locationLabel = (location: AccessibleLocation) => location.name || location.label || location.location_key || location.id
const addressText = (location: OnboardingLocationPayload) => [location.address, location.city, location.region, location.country].filter(Boolean).join(', ')
const isValidLatitude = (value: unknown) => Number.isFinite(Number(value)) && Number(value) >= -90 && Number(value) <= 90
const isValidLongitude = (value: unknown) => Number.isFinite(Number(value)) && Number(value) >= -180 && Number(value) <= 180
const isValidRadius = (value: unknown) => Number.isFinite(Number(value)) && Number(value) >= MIN_GEOFENCE_RADIUS_METERS && Number(value) <= MAX_GEOFENCE_RADIUS_METERS
const formatCoordinate = (value: number) => Number(value.toFixed(6))
const isErrorMessage = (value: string) => /Unable|Latitude|Longitude|Radius|required/.test(value)

type GeocodeStatus = {
  loading?: boolean
  resolved?: boolean
  warning?: string
  message?: string
}

export const SetupWizard: React.FC = () => {
  const { accessContext, currentOrganization, refresh, loading, error } = useAccessContext()
  const { t } = useI18n()
  const onboarding = accessContext?.onboarding
  const organizations = accessContext?.visibleOrganizations || []
  const accessibleLocations = accessContext?.accessibleLocations || []
  const completed = new Set(accessContext?.completedSetupSteps || onboarding?.completedSetupSteps || [])
  const recommended = accessContext?.currentRecommendedStep || onboarding?.currentRecommendedStep || 'company_profile'
  const normalizedRecommended = recommended === 'company_profile' || recommended === 'organization_defaults' ? 'workspace_profile' : recommended === 'supervisors' ? 'users_roles' : recommended === 'review_launch' ? 'readiness' : recommended
  const initialStep = Math.max(0, steps.findIndex((step) => step.key === normalizedRecommended))
  const [stepIndex, setStepIndex] = useState(initialStep)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [localOnboarding, setLocalOnboarding] = useState<TenantOnboardingState | null>(onboarding || null)
  const [readiness, setReadiness] = useState<any>(null)
  const [profileConfirmation, setProfileConfirmation] = useState<CompanyProfileConfirmation>()
  const [companyProfile, setCompanyProfile] = useState({
    organizationName: organizations[0]?.name || '',
    companyType: (onboarding?.companyType || onboarding?.company_type || 'construction') as CompanyType,
    operatingModel: (onboarding?.operatingModel || onboarding?.operating_model || (organizations[0]?.org_type === 'agency' ? 'agency_only' : 'company_only')) as OperatingModel,
    workerCount: Number(onboarding?.companySize || onboarding?.company_size || 25),
    locationCount: Number(onboarding?.locationCountPlanned || onboarding?.location_count_planned || Math.max(1, accessibleLocations.length || 1)),
    supervisorCount: Number(onboarding?.supervisorCountPlanned || onboarding?.supervisor_count_planned || 1),
  })
  const [locations, setLocations] = useState<OnboardingLocationPayload[]>(() => {
    if (accessibleLocations.length > 0) {
      return accessibleLocations.map((location) => {
        const address = normalizeLocationAddress(location)
        return {
          id: location.id,
          name: location.name || location.label || '',
          address: address.line1 || address.formattedAddress || '',
          city: address.city || '',
          region: address.region || '',
          country: address.countryCode || String((location.metadata as any)?.country || ''),
          timezone: String(location.timezone || (location.metadata as any)?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'),
          defaultPunchRadius: firstNumber((location.metadata as any)?.defaultPunchRadius, location.radius_meters, DEFAULT_GEOFENCE_RADIUS_METERS),
          latitude: firstNumber((location as any).center_lat, address.latitude ?? undefined, (location.metadata as any)?.latitude),
          longitude: firstNumber((location as any).center_lon, address.longitude ?? undefined, (location.metadata as any)?.longitude),
          ownerOrgId: location.ownerOrgId || location.owner_org_id || undefined,
        }
      })
    }
    return [blankLocation()]
  })
  // A user-selected assistant proposal only fills this reviewable form. Existing save validation remains authoritative.
  useEffect(() => {
    const review = () => {
      const raw = sessionStorage.getItem('punch-ai-setup-proposal');
      if (!raw) return;
      sessionStorage.removeItem('punch-ai-setup-proposal');
      try {
        const proposal = JSON.parse(raw), draft = proposal.draft;
        if (proposal.identityKey !== `${accessContext?.currentUser?.id}:${accessContext?.tenantId}` || proposal.organizationId !== (currentOrganization?.id || null) || draft?.step !== 'workspace_profile'
          || typeof draft.organizationName !== 'string' || !draft.organizationName.trim() || draft.organizationName.length > 160
          || !['construction','staffing_agency','security','restaurant_franchise','warehouse_logistics','retail','other'].includes(draft.companyType)
          || !['company_only','agency_only','company_with_agencies','subcontractor_network'].includes(draft.operatingModel)) return;
        setCompanyProfile(previous => ({...previous, organizationName:draft.organizationName,companyType:draft.companyType,operatingModel:draft.operatingModel}));
        setStepIndex(0); setMessage('Punch AI proposal loaded. Review these fields before saving.');
      } catch { /* Invalid or stale proposals do not alter the form. */ }
    };
    review(); window.addEventListener('punch-ai-review-draft',review);
    return () => window.removeEventListener('punch-ai-review-draft',review);
  }, [currentOrganization?.id, accessContext?.currentUser?.id, accessContext?.tenantId]);
  const [supervisors, setSupervisors] = useState<OnboardingSupervisorPayload[]>([blankSupervisor()])
  const [workers, setWorkers] = useState<OnboardingWorkerPayload[]>([blankWorker()])
  const [firstJobDraft, setFirstJobDraft] = useState({ title: '', locationId: accessibleLocations[0]?.id || '', shiftDate: '' })
  const [geocodeStatus, setGeocodeStatus] = useState<Record<number, GeocodeStatus>>({})
  const [advancedCoordinatesOpen, setAdvancedCoordinatesOpen] = useState<Record<number, boolean>>({})
  const [mapUnavailable, setMapUnavailable] = useState(false)

  useEffect(() => {
    setLocalOnboarding(onboarding || null)
  }, [onboarding?.updatedAt, onboarding?.status])

  useEffect(() => {
    if (!accessContext?.tenantId) return
    if (onboarding?.status && onboarding.status !== 'not_started') return
    startOnboarding().then(setLocalOnboarding).catch(() => undefined)
  }, [accessContext?.tenantId])

  useEffect(() => {
    if (!accessContext?.tenantId) return
    getOnboardingReadiness().then(setReadiness).catch(() => undefined)
  }, [accessContext?.tenantId, onboarding?.updatedAt, localOnboarding?.updatedAt])

  const activeStep = steps[stepIndex] || steps[0]
  const readinessComplete = new Set((readiness?.checks || []).filter((check: any) => check.status === 'complete').map((check: any) => check.key))
  const localCompletedSteps = new Set(localOnboarding?.completedSetupSteps || [])
  const isStepComplete = (key: OnboardingStepKey) => {
    if (key === 'workspace_profile') return completed.has('workspace_profile') || completed.has('company_profile') || localCompletedSteps.has('company_profile') || readinessComplete.has('organization_profile')
    if (key === 'organization_profile') return completed.has('organization_profile') || completed.has('organization_defaults') || localCompletedSteps.has('organization_defaults') || readinessComplete.has('organization_profile')
    if (key === 'users_roles') return completed.has('users_roles') || completed.has('supervisors') || localCompletedSteps.has('supervisors') || readinessComplete.has('users_roles')
    return completed.has(key) || localCompletedSteps.has(key) || readinessComplete.has(key)
  }
  const completedCount = steps.filter((step) => isStepComplete(step.key)).length
  const percentComplete = readiness?.percentComplete ?? Math.round((completedCount / steps.length) * 100)
  const canSkip = Boolean(accessContext?.tenantMemberships?.some((membership) => membership.membership_type === 'tenant_admin') || accessContext?.isSystemSuperAdmin)
  const primaryOrgId = localOnboarding?.primaryOrgId || localOnboarding?.primary_org_id || organizations[0]?.id || ''
  const openAdminPage = (url: string) => {
    navigateTo(url)
  }
  const updateLocation = (index: number, patch: Partial<OnboardingLocationPayload>) => {
    setLocations((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row))
  }
  const geocodeLocation = async (index: number) => {
    const location = locations[index]
    const query = addressText(location)
    if (!query.trim()) {
      setGeocodeStatus((current) => ({ ...current, [index]: { warning: 'Enter an address before finding it on the map.' } }))
      return
    }
    setGeocodeStatus((current) => ({ ...current, [index]: { loading: true } }))
    try {
      const params = new URLSearchParams({ format: 'jsonv2', limit: '1', q: query })
      const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`)
      const results = await response.json()
      const match = Array.isArray(results) ? results[0] : null
      const lat = match ? Number(match.lat) : NaN
      const lon = match ? Number(match.lon) : NaN
      if (!isValidLatitude(lat) || !isValidLongitude(lon)) {
        setGeocodeStatus((current) => ({ ...current, [index]: { warning: GEOCODE_ERROR } }))
        return
      }
      updateLocation(index, {
        latitude: formatCoordinate(lat),
        longitude: formatCoordinate(lon),
        defaultPunchRadius: location.defaultPunchRadius || DEFAULT_GEOFENCE_RADIUS_METERS,
      })
      setGeocodeStatus((current) => ({
        ...current,
        [index]: { resolved: true, message: `Worksite destination resolved. A separate ${location.defaultPunchRadius || DEFAULT_GEOFENCE_RADIUS_METERS}m punch geofence will default to this point.` },
      }))
    } catch {
      setGeocodeStatus((current) => ({ ...current, [index]: { warning: GEOCODE_ERROR } }))
    }
  }
  const validationMessageFor = (location: OnboardingLocationPayload) => {
    if ((location.latitude !== undefined && location.longitude === undefined) || (location.latitude === undefined && location.longitude !== undefined)) return 'Latitude and longitude are both required to create a geofence.'
    if (location.latitude !== undefined && !isValidLatitude(location.latitude)) return 'Latitude must be between -90 and 90.'
    if (location.longitude !== undefined && !isValidLongitude(location.longitude)) return 'Longitude must be between -180 and 180.'
    if (location.defaultPunchRadius !== undefined && !isValidRadius(location.defaultPunchRadius)) return `Radius must be between ${MIN_GEOFENCE_RADIUS_METERS} and ${MAX_GEOFENCE_RADIUS_METERS} meters.`
    return ''
  }
  const fallbackMapUrl = (location: OnboardingLocationPayload) => {
    const query = isValidLatitude(location.latitude) && isValidLongitude(location.longitude)
      ? `${location.latitude},${location.longitude}`
      : encodeURIComponent(addressText(location))
    return `https://www.openstreetmap.org/search?query=${query}`
  }

  const run = async (action: () => Promise<unknown>, success: string) => {
    setSaving(true)
    setMessage('')
    try {
      const result: any = await action()
      if (result?.onboarding) setLocalOnboarding(result.onboarding)
      if (result?.status) setLocalOnboarding(result)
      setMessage(success)
      await refresh()
      getOnboardingReadiness().then(setReadiness).catch(() => undefined)
      return result
    } catch (err) {
      setMessage(apiErrorMessage(err, 'Unable to save setup.'))
      return null
    } finally {
      setSaving(false)
    }
  }

  const saveCurrentStep = async () => {
    if (activeStep.key === 'workspace_profile' || activeStep.key === 'organization_profile' || activeStep.key === 'company_profile' || activeStep.key === 'organization_defaults') {
      return Boolean(await run(async () => {
        const result = await saveOnboardingCompanyProfile({ ...companyProfile, primaryOrgId: primaryOrgId || undefined, businessProfileConfirmation: profileConfirmation })
        setProfileConfirmation(undefined)
        return result
      }, 'Company profile saved.'))
    }
    if (activeStep.key === 'branding') {
      return Boolean(await run(async () => updateOnboardingStatus({ completedSteps: ['branding'], currentStep: 'locations', wizardPayload: { brandingReviewed: true } }), 'Branding step saved.'))
    }
    if (activeStep.key === 'locations') {
      const invalidLocation = locations.find((location) => validationMessageFor(location))
      if (invalidLocation) {
        setMessage(validationMessageFor(invalidLocation))
        return false
      }
      const result: any = await run(async () => {
        const locationPayload = locations.filter((location) => location.name.trim())
        const response: any = await saveOnboardingLocations({ primaryOrgId: primaryOrgId || undefined, locations: locationPayload })
        if (Array.isArray(response?.locations)) {
          for (let index = 0; index < locationPayload.length; index += 1) {
            const location = locationPayload[index]
            const savedLocationId = response.locations[index]?.id || location.id
            if (!savedLocationId || location.latitude === undefined || location.longitude === undefined || !location.defaultPunchRadius) continue
            await saveOnboardingGeofence(savedLocationId, {
              center: { lat: Number(location.latitude), lon: Number(location.longitude) },
              radiusMeters: Number(location.defaultPunchRadius || DEFAULT_GEOFENCE_RADIUS_METERS),
            })
          }
        }
        return response
      }, 'Locations saved.')
      if (Array.isArray(result?.locations)) {
        setLocations((current) => current.map((location, index) => ({
          ...location,
          id: result.locations[index]?.id || location.id,
          ownerOrgId: result.locations[index]?.owner_org_id || result.locations[index]?.ownerOrgId || location.ownerOrgId,
        })))
      }
      return Boolean(result)
    }
    if (activeStep.key === 'geofences') {
      const invalidLocation = locations.find((location) => validationMessageFor(location))
      if (invalidLocation) {
        setMessage(validationMessageFor(invalidLocation))
        return false
      }
      const targets = locations.filter((location) => location.id && location.latitude !== undefined && location.longitude !== undefined && location.defaultPunchRadius)
      if (targets.length === 0) {
        return Boolean(await run(async () => updateOnboardingStatus({ completedSteps: ['geofences'], currentStep: 'supervisors', wizardPayload: { geofenceWarnings: ['manual_coordinates_missing'] } }), 'Geofence warnings saved.'))
      }
      return Boolean(await run(async () => {
        for (const location of targets) {
          await saveOnboardingGeofence(location.id!, {
            center: { lat: Number(location.latitude), lon: Number(location.longitude) },
            radiusMeters: Number(location.defaultPunchRadius || DEFAULT_GEOFENCE_RADIUS_METERS),
          })
        }
      }, 'Geofences saved.'))
    }
    if (activeStep.key === 'users_roles' || activeStep.key === 'supervisors') {
      return Boolean(await run(async () => saveOnboardingSupervisors({
        supervisors: supervisors
          .filter((supervisor) => supervisor.email || supervisor.phone)
          .map((supervisor) => ({ ...supervisor, orgId: supervisor.orgId || primaryOrgId || undefined })),
      }), 'Supervisors saved.'))
    }
    if (activeStep.key === 'workers') {
      const payload = workers.filter((worker) => worker.email || worker.phone)
      return Boolean(await run(async () => payload.length ? saveOnboardingWorkers({ workers: payload }) : updateOnboardingStatus({ completedSteps: ['workers'], currentStep: 'first_job' }), payload.length ? 'Workers saved.' : 'Worker step skipped.'))
    }
    if (activeStep.key === 'first_job') {
      return Boolean(await run(async () => saveOnboardingFirstJob({ skipped: !firstJobDraft.title, draft: firstJobDraft }), firstJobDraft.title ? 'First job draft saved.' : 'First job step saved.'))
    }
    if (activeStep.key === 'first_shift') {
      return Boolean(await run(async () => updateOnboardingStatus({ completedSteps: ['first_shift'], currentStep: 'approvals', wizardPayload: { firstShiftDraft: firstJobDraft } }), 'First shift step saved.'))
    }
    if (activeStep.key === 'approvals') {
      return Boolean(await run(async () => updateOnboardingStatus({ completedSteps: ['approvals'], currentStep: 'exports', wizardPayload: { approvalsReviewed: true } }), 'Approval settings saved.'))
    }
    if (activeStep.key === 'exports') {
      return Boolean(await run(async () => updateOnboardingStatus({ completedSteps: ['exports'], currentStep: 'billing_plan', wizardPayload: { exportsReviewed: true } }), 'Reporting/export settings saved.'))
    }
    if (activeStep.key === 'billing_plan') {
      return Boolean(await run(async () => saveOnboardingBillingPlan({ planKey: 'free', billingFrequency: 'monthly' }), 'Free access confirmed.'))
    }
    const completedSetup = await run(async () => completeOnboarding(), readiness?.completed === false ? 'Setup marked complete. Review the remaining readiness warnings.' : 'Setup complete.')
    if (!completedSetup) return false
    navigateTo('/admin')
    return true
  }

  const saveAndNext = async () => {
    const saved = await saveCurrentStep()
    if (!saved) return
    setStepIndex((index) => Math.min(index + 1, steps.length - 1))
  }

  const summary = useMemo(() => ({
    organization: companyProfile.organizationName || organizations[0]?.name || t('Not set'),
    locations: locations.filter((location) => location.name.trim()).length,
    geofences: locations.filter((location) => location.latitude !== undefined && location.longitude !== undefined).length,
    supervisors: supervisors.filter((supervisor) => supervisor.email || supervisor.phone).length,
    workers: workers.filter((worker) => worker.email || worker.phone).length,
    firstJob: firstJobDraft.title || t('Skipped'),
  }), [companyProfile.organizationName, organizations, locations, supervisors, workers, firstJobDraft.title, t])

  if (loading) {
    return <section className="panel workspace-panel stack"><h2 className="heading-lg">{t('Setup Wizard')}</h2><p className="text-muted">{t('Loading setup context...')}</p></section>
  }

  if (error) {
    return <section className="panel workspace-panel stack"><h2 className="heading-lg">{t('Setup Wizard')}</h2><p className="error-text">{error}</p><button className="btn btn-secondary" onClick={refresh}>{t('Retry')}</button></section>
  }

  return (
    <section className="setup-wizard stack" data-testid="setup-wizard">
      <div className="setup-header">
        <div>
          <span className="helper">{t('NexusWorkforce360 setup')}</span>
          <h2 className="heading-lg">{t('Setup Wizard')}</h2>
          <p className="text-muted">{t('Progress saves after each step. You can return here from Admin Settings later.')}</p>
        </div>
        <div className="setup-header-actions">
          <span className={`status-pill status-pill--${localOnboarding?.status || accessContext?.onboardingStatus || 'not_started'}`}>
            {t(humanize(localOnboarding?.status || accessContext?.onboardingStatus || 'not_started'))}
          </span>
          {canSkip ? <button className="btn btn-secondary" disabled={saving} onClick={() => run(skipOnboarding, t('Setup skipped.'))}>{t('Skip')}</button> : null}
          <button className="btn btn-ghost" onClick={() => navigateTo('/admin')}>{t('Finish later')}</button>
        </div>
      </div>

      <div className="setup-progress" aria-label={t('Setup progress')}>
        {steps.map((step, index) => (
          <button
            key={step.key}
            type="button"
            className={`setup-step ${index === stepIndex ? 'active' : ''} ${isStepComplete(step.key) ? 'complete' : ''}`}
            onClick={() => setStepIndex(index)}
          >
            <span>{index + 1}</span>
            <strong>{t(step.label)}</strong>
            <em>{isStepComplete(step.key) ? t('Complete') : step.optional ? t('Optional') : t('Required')}</em>
          </button>
        ))}
      </div>
      <div className="setup-percent-row">
        <strong>{percentComplete}% {t('complete')}</strong>
        <span className="helper">{t('{completed} / {total} steps touched', { completed: completedCount, total: steps.length })}</span>
      </div>
      <div className="setup-meter" aria-hidden="true"><span style={{ width: `${percentComplete}%` }} /></div>

      {message ? <p className={isErrorMessage(message) ? 'error-text' : 'success-text'}>{message}</p> : null}

      <div className="setup-step-toolbar">
        <div>
          <span className="helper">{activeStep.optional ? t('Optional step') : t('Required step')}</span>
          <h3>{t(activeStep.label)}</h3>
        </div>
        {activeStep.actionUrl ? (
          <button type="button" className="btn btn-secondary" onClick={() => openAdminPage(activeStep.actionUrl!)}>
            {t('Open admin page')}
          </button>
        ) : null}
      </div>

      <div className="setup-body">
        {activeStep.key === 'workspace_profile' || activeStep.key === 'organization_profile' ? (
          <div className="setup-form-grid">
            <label>{t('Organization name')}<input className="input" value={companyProfile.organizationName} onChange={(event) => setCompanyProfile({ ...companyProfile, organizationName: event.target.value })} /></label>
            <label>{t('Company type')}<select className="input" value={companyProfile.companyType} onChange={(event) => setCompanyProfile({ ...companyProfile, companyType: event.target.value as CompanyType })}>{companyTypes.map((value) => <option key={value} value={value}>{t(humanize(value))}</option>)}</select></label>
            <label>{t('Operating model')}<select className="input" value={companyProfile.operatingModel} onChange={(event) => setCompanyProfile({ ...companyProfile, operatingModel: event.target.value as OperatingModel })}>{operatingModels.map((value) => <option key={value} value={value}>{t(humanize(value))}</option>)}</select></label>
            <label>{t('Approx. workers')}<input className="input" type="number" min={0} value={companyProfile.workerCount} onChange={(event) => setCompanyProfile({ ...companyProfile, workerCount: Number(event.target.value) })} /></label>
            <label>{t('Approx. locations')}<input className="input" type="number" min={0} value={companyProfile.locationCount} onChange={(event) => setCompanyProfile({ ...companyProfile, locationCount: Number(event.target.value) })} /></label>
            <label>{t('Approx. supervisors')}<input className="input" type="number" min={0} value={companyProfile.supervisorCount} onChange={(event) => setCompanyProfile({ ...companyProfile, supervisorCount: Number(event.target.value) })} /></label>
            <CompanyProfileEnrichment key={primaryOrgId || 'new'} orgId={primaryOrgId || undefined} onConfirmDraft={confirmation => {
              setProfileConfirmation(confirmation)
              const name = confirmation.fields.legalName || confirmation.fields.tradingName
              if (typeof name === 'string') setCompanyProfile(previous => ({ ...previous, organizationName: name }))
            }} />
          </div>
        ) : null}

        {activeStep.key === 'branding' ? (
          <div className="stack">
            <p className="text-muted">Branding is managed on the existing Settings page. Review display name, logo, colors, support contacts, and mark this step done when the initial brand is ready.</p>
            <div className="setup-summary-grid">
              <div><span className="helper">Primary organization</span><strong>{organizations.find((org) => org.id === primaryOrgId)?.name || companyProfile.organizationName || 'Not set'}</strong></div>
              <div><span className="helper">Readiness</span><strong>{readinessComplete.has('branding') ? 'Configured' : 'Needs review'}</strong></div>
            </div>
          </div>
        ) : null}

        {activeStep.key === 'organization_defaults' ? (
          <div className="stack">
            {primaryOrgId?<ActivationGuidance key={`activation:${primaryOrgId}`} orgId={primaryOrgId}/>:null}
            {primaryOrgId ? <IndustryWorkspaceTemplates key={primaryOrgId} orgId={primaryOrgId} /> : null}
            <p className="text-muted">The wizard will use the primary organization, preserve any existing organizations, and ensure root organization defaults are present.</p>
            <div className="setup-summary-grid">
              <div><span className="helper">Primary organization</span><strong>{organizations.find((org) => org.id === primaryOrgId)?.name || companyProfile.organizationName || 'Will be created'}</strong></div>
              <div><span className="helper">Operating model</span><strong>{humanize(companyProfile.operatingModel)}</strong></div>
              <div><span className="helper">Existing organizations</span><strong>{organizations.length}</strong></div>
            </div>
          </div>
        ) : null}

        {activeStep.key === 'locations' ? (
          <div className="stack">
            {locations.map((location, index) => (
              <div key={index} className="setup-geofence-card">
                <div className="setup-form-grid">
                  <label>Location name<input className="input" value={location.name} onChange={(event) => updateLocation(index, { name: event.target.value })} /></label>
                  <label>Address<input className="input" value={location.address || ''} onBlur={() => geocodeLocation(index)} onChange={(event) => updateLocation(index, { address: event.target.value })} /></label>
                  <label>Geofence radius meters<input className="input" type="number" min={MIN_GEOFENCE_RADIUS_METERS} max={MAX_GEOFENCE_RADIUS_METERS} value={location.defaultPunchRadius ?? DEFAULT_GEOFENCE_RADIUS_METERS} onChange={(event) => updateLocation(index, { defaultPunchRadius: Number(event.target.value) })} /></label>
                </div>
                <span className="helper">Worksite address is used for matching, maps, and travel. The separate punch geofence defaults to these coordinates and can be adjusted independently.</span>
                <button type="button" className="btn btn-secondary setup-inline-action" disabled={geocodeStatus[index]?.loading} onClick={() => geocodeLocation(index)}>{geocodeStatus[index]?.loading ? 'Finding address...' : 'Find address on map'}</button>
                <LeafletGeofencePicker
                  latitude={location.latitude}
                  longitude={location.longitude}
                  radiusMeters={Number(location.defaultPunchRadius || DEFAULT_GEOFENCE_RADIUS_METERS)}
                  label={location.name || `Location ${index + 1}`}
                  onChange={(point) => updateLocation(index, { latitude: point.latitude, longitude: point.longitude })}
                  onMapUnavailable={() => setMapUnavailable(true)}
                />
                <details className="setup-advanced-coordinates" open={Boolean(advancedCoordinatesOpen[index])} onToggle={(event) => {
                  const open = event.currentTarget.open
                  setAdvancedCoordinatesOpen((current) => current[index] === open ? current : { ...current, [index]: open })
                }}>
                  <summary>Advanced: enter coordinates manually</summary>
                  <div className="setup-form-grid">
                    <label>Latitude<input className="input" type="number" step="0.000001" value={location.latitude ?? ''} onChange={(event) => updateLocation(index, { latitude: event.target.value ? Number(event.target.value) : undefined })} /></label>
                    <label>Longitude<input className="input" type="number" step="0.000001" value={location.longitude ?? ''} onChange={(event) => updateLocation(index, { longitude: event.target.value ? Number(event.target.value) : undefined })} /></label>
                  </div>
                </details>
                {mapUnavailable ? <p className="helper">Fallback only: the embedded map could not load. <a href={fallbackMapUrl(location)} target="_blank" rel="noreferrer">Open external map</a> and paste coordinates in Advanced.</p> : null}
                {validationMessageFor(location) ? <span className="error-text">{validationMessageFor(location)}</span> : null}
                {geocodeStatus[index]?.message ? <span className="success-text">{geocodeStatus[index]?.message}</span> : null}
                {geocodeStatus[index]?.warning ? <span className="error-text">{geocodeStatus[index]?.warning}</span> : null}
              </div>
            ))}
            <button className="btn btn-secondary" onClick={() => setLocations([...locations, blankLocation()])}>Add location</button>
          </div>
        ) : null}

        {activeStep.key === 'geofences' ? (
          <div className="stack">
            <p className="text-muted">Worksite address is used for matching, maps, and travel. Punch geofence is used only for attendance validation and can be adjusted independently.</p>
            {locations.map((location, index) => (
              <div key={location.id || index} className="setup-geofence-card">
                <div className="setup-geofence-header">
                  <div>
                    <strong>{location.name || `Location ${index + 1}`}</strong>
                    <span className="helper">{addressText(location) || 'Address not set'}</span>
                  </div>
                  <button type="button" className="btn btn-secondary" disabled={geocodeStatus[index]?.loading} onClick={() => geocodeLocation(index)}>{geocodeStatus[index]?.loading ? 'Finding address...' : 'Find address on map'}</button>
                </div>
                <div className="setup-form-grid">
                  <label>Geofence radius meters<input className="input" type="number" min={MIN_GEOFENCE_RADIUS_METERS} max={MAX_GEOFENCE_RADIUS_METERS} value={location.defaultPunchRadius ?? DEFAULT_GEOFENCE_RADIUS_METERS} onChange={(event) => updateLocation(index, { defaultPunchRadius: Number(event.target.value) })} /></label>
                </div>
                <LeafletGeofencePicker
                  latitude={location.latitude}
                  longitude={location.longitude}
                  radiusMeters={Number(location.defaultPunchRadius || DEFAULT_GEOFENCE_RADIUS_METERS)}
                  label={location.name || `Location ${index + 1}`}
                  onChange={(point) => updateLocation(index, { latitude: point.latitude, longitude: point.longitude })}
                  onMapUnavailable={() => setMapUnavailable(true)}
                />
                <details className="setup-advanced-coordinates" open={Boolean(advancedCoordinatesOpen[index])} onToggle={(event) => {
                  const open = event.currentTarget.open
                  setAdvancedCoordinatesOpen((current) => current[index] === open ? current : { ...current, [index]: open })
                }}>
                  <summary>Advanced: enter coordinates manually</summary>
                  <div className="setup-form-grid">
                    <label>Latitude<input className="input" type="number" step="0.000001" value={location.latitude ?? ''} onChange={(event) => updateLocation(index, { latitude: event.target.value ? Number(event.target.value) : undefined })} /></label>
                    <label>Longitude<input className="input" type="number" step="0.000001" value={location.longitude ?? ''} onChange={(event) => updateLocation(index, { longitude: event.target.value ? Number(event.target.value) : undefined })} /></label>
                  </div>
                </details>
                {mapUnavailable ? <p className="helper">Fallback only: the embedded map could not load. <a href={fallbackMapUrl(location)} target="_blank" rel="noreferrer">Open external map</a> and paste coordinates in Advanced.</p> : null}
                {validationMessageFor(location) ? <p className="error-text">{validationMessageFor(location)}</p> : null}
                {geocodeStatus[index]?.message ? <p className="success-text">{geocodeStatus[index]?.message}</p> : null}
                {geocodeStatus[index]?.warning ? <p className="error-text">{geocodeStatus[index]?.warning}</p> : null}
                {location.latitude === undefined || location.longitude === undefined ? <span className="helper">No geofence yet; punch-in may fail for this location.</span> : null}
              </div>
            ))}
          </div>
        ) : null}

        {activeStep.key === 'users_roles' ? (
          <div className="stack">
            <p className="text-muted">Invite an admin or supervisor, then tune detailed roles in Users & Access.</p>
            {supervisors.map((supervisor, index) => (
              <div key={index} className="setup-row">
                <input className="input" placeholder="First name" value={supervisor.firstName || ''} onChange={(event) => setSupervisors((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, firstName: event.target.value } : row))} />
                <input className="input" placeholder="Last name" value={supervisor.lastName || ''} onChange={(event) => setSupervisors((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, lastName: event.target.value } : row))} />
                <input className="input" type="email" placeholder="Email" value={supervisor.email || ''} onChange={(event) => setSupervisors((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, email: event.target.value } : row))} />
                <select className="input" value={supervisor.locationId || ''} onChange={(event) => setSupervisors((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, locationId: event.target.value || undefined } : row))}>
                  <option value="">Organization scope</option>
                  {accessibleLocations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}
                </select>
              </div>
            ))}
            <button className="btn btn-secondary" onClick={() => setSupervisors([...supervisors, blankSupervisor()])}>Add supervisor</button>
          </div>
        ) : null}

        {activeStep.key === 'workers' ? (
          <div className="stack">
            <p className="text-muted">Add a few first workers now, or use the Workers page for a larger import/invite workflow.</p>
            {workers.map((worker, index) => (
              <div key={index} className="setup-row">
                <input className="input" placeholder="First name" value={worker.firstName || ''} onChange={(event) => setWorkers((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, firstName: event.target.value } : row))} />
                <input className="input" placeholder="Last name" value={worker.lastName || ''} onChange={(event) => setWorkers((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, lastName: event.target.value } : row))} />
                <input className="input" type="email" placeholder="Email" value={worker.email || ''} onChange={(event) => setWorkers((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, email: event.target.value } : row))} />
                <input className="input" placeholder="Phone" value={worker.phone || ''} onChange={(event) => setWorkers((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, phone: event.target.value } : row))} />
              </div>
            ))}
            <button className="btn btn-secondary" onClick={() => setWorkers([...workers, blankWorker()])}>Add worker</button>
          </div>
        ) : null}

        {activeStep.key === 'first_job' ? (
          <div className="setup-form-grid">
            <label>Job/order title<input className="input" value={firstJobDraft.title} onChange={(event) => setFirstJobDraft({ ...firstJobDraft, title: event.target.value })} placeholder="Optional" /></label>
            <label>Location<select className="input" value={firstJobDraft.locationId} onChange={(event) => setFirstJobDraft({ ...firstJobDraft, locationId: event.target.value })}><option value="">Not selected</option>{accessibleLocations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}</select></label>
            <label>First shift date<input className="input" type="date" value={firstJobDraft.shiftDate} onChange={(event) => setFirstJobDraft({ ...firstJobDraft, shiftDate: event.target.value })} /></label>
          </div>
        ) : null}

        {activeStep.key === 'first_shift' ? (
          <div className="setup-form-grid">
            <label>Job/order title<input className="input" value={firstJobDraft.title} onChange={(event) => setFirstJobDraft({ ...firstJobDraft, title: event.target.value })} placeholder="Optional draft label" /></label>
            <label>Location<select className="input" value={firstJobDraft.locationId} onChange={(event) => setFirstJobDraft({ ...firstJobDraft, locationId: event.target.value })}><option value="">Not selected</option>{accessibleLocations.map((location) => <option key={location.id} value={location.id}>{locationLabel(location)}</option>)}</select></label>
            <label>First shift date<input className="input" type="date" value={firstJobDraft.shiftDate} onChange={(event) => setFirstJobDraft({ ...firstJobDraft, shiftDate: event.target.value })} /></label>
          </div>
        ) : null}

        {activeStep.key === 'approvals' ? (
          <div className="stack">
            <p className="text-muted">Approval workflow defaults are available through Timesheets. Confirm who reviews completed shifts and mark this setup step complete.</p>
            <button type="button" className="btn btn-secondary" onClick={() => openAdminPage('/admin/timesheets')}>Open Timesheets</button>
          </div>
        ) : null}

        {activeStep.key === 'exports' ? (
          <div className="stack">
            <p className="text-muted">Payroll/export settings use the existing Payroll Export and Reports surfaces. Confirm export format expectations before launch.</p>
            <button type="button" className="btn btn-secondary" onClick={() => openAdminPage('/admin/payroll')}>Open Payroll Export</button>
          </div>
        ) : null}

        {activeStep.key === 'billing_plan' ? (
          <div className="stack">
            <article className="setup-choice active"><span className="helper">Current plan</span><strong>You’re on Free</strong><span>$0 base platform access</span><p className="text-muted">Workers, schedules, jobs, locations, punches, timesheets, approvals, and normal workforce relationships are included without per-worker or per-location pricing.</p></article>
            <article className="panel stack"><strong>Explore acceleration when you need it</strong><p className="text-muted">Optional paid acceleration provides priority marketplace processing, faster matching and auto-fill, higher report concurrency, deeper automation, and faster integration synchronization.</p><button type="button" className="btn btn-secondary" onClick={() => openAdminPage('/billing')}>Explore acceleration and add-ons</button></article>
          </div>
        ) : null}

        {activeStep.key === 'readiness' ? (
          <div className="setup-summary-grid">
            <div><span className="helper">Organization</span><strong>{summary.organization}</strong></div>
            <div><span className="helper">Locations</span><strong>{summary.locations}</strong></div>
            <div><span className="helper">Geofences</span><strong>{summary.geofences}</strong></div>
            <div><span className="helper">Supervisors</span><strong>{summary.supervisors}</strong></div>
            <div><span className="helper">Workers</span><strong>{summary.workers}</strong></div>
            <div><span className="helper">First job/shift</span><strong>{summary.firstJob}</strong></div>
            {readiness?.checks?.map((check: any) => (
              <div key={check.key} className={check.status === 'complete' ? 'complete' : ''}>
                <span className="helper">{check.required ? 'Required' : 'Optional'}</span>
                <strong>{check.label}</strong>
                <span>{humanize(check.status)}</span>
              </div>
            ))}
            {localOnboarding?.status === 'completed' && readiness?.completed === false ? (
              <p className="error-text">Setup is marked complete, but one or more readiness checks still need attention.</p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="setup-actions">
        <button className="btn btn-secondary" disabled={saving || stepIndex === 0} onClick={() => setStepIndex((index) => Math.max(0, index - 1))}>Back</button>
        <button className="btn btn-secondary" disabled={saving} onClick={saveCurrentStep}>Save</button>
        <button className="btn btn-primary" disabled={saving} onClick={saveAndNext}>{activeStep.key === 'readiness' ? 'Complete setup' : activeStep.optional ? 'Save or skip' : 'Save & continue'}</button>
      </div>
    </section>
  )
}
